import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Download, Home, FileText, CheckCircle, Calendar, CreditCard, Package, Mail, Hash, QrCode, Ticket as TicketIcon } from 'lucide-react';
import { getApiBaseUrl } from '../config/apiConfig';
import { usePaymentListener } from '../hooks/usePaymentListener';
import { PEEKPASS_BRAND } from '../utils/peekPassBrand';

// 🎯 INTERFACES
interface PaymentItem {
  item_id: string;
  title: string;
  description?: string;
  quantity: number;
  unit_price: number;
  total: number;
}

interface PaymentDetails {
  mercadopago_payment_id: string;
  mercadopago_preference_id?: string;
  external_reference: string;
  status: string;
  status_detail: string;
  amount: number;
  currency: string;
  payment_method_id?: string;
  payment_type_name?: string;
  date_created: string;
  date_approved?: string;
  user_email: string;
  user?: {
    email: string;
    firstName: string;
    lastName: string;
    fullName: string;
  };
  items: PaymentItem[];
  transaction_details?: {
    installments?: number;
    net_received_amount?: number;
    total_paid_amount?: number;
  };
    order?: {
      id: string;
      status: string;
      organizerAmount?: number;
      serviceFeeAmount?: number;
      totalPaid?: number;
      ticketsGenerated?: boolean;
      notificationsSent?: boolean;
      stockDeducted?: boolean;
      manualReview?: {
        required?: boolean;
        reason?: string;
        message?: string;
        conflicts?: {
          eventTitle?: string;
          ticketTierName?: string;
          requested?: number;
          available?: number;
          reason?: string;
        }[];
      } | null;
      priceBreakdown?: {
        ticketSubtotal?: number;
        serviceFeeAmount?: number;
        totalPaid?: number;
        organizerAmount?: number;
    };
  } | null;
  tickets?: {
    ticketId: string;
    eventTitle: string;
    eventDate?: string;
    eventAddress?: string;
    ticketTierName?: string;
    qrUrl?: string;
    qrImageUrl?: string;
      status: string;
    }[];
    product_delivered?: boolean;
    manual_review?: boolean;
    userMessage?: string | null;
    processing?: boolean;
  }

interface LocationState {
  paymentDetails?: PaymentDetails;
  message?: string;
}

const PaymentSuccess: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const state = location.state as LocationState;
  const printRef = useRef<HTMLDivElement>(null);
  
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(state?.paymentDetails || null);
  const [isLoading, setIsLoading] = useState(!state?.paymentDetails);
  const [statusMessage, setStatusMessage] = useState('');
  const message = state?.message || '¡Tu pago fue procesado exitosamente!';

  usePaymentListener({
    paymentId:
      searchParams.get('external_reference') ||
      searchParams.get('payment_id') ||
      '',
    onPaymentComplete: (status) => {
      console.log('🎉 WebSocket recibió pago aprobado', status);

      setPaymentDetails((prev:any)=>({
        ...(prev||{}),
        status: status.status,
        status_detail: status.statusDetail,
        amount: status.amount,
        currency: status.currency,
        external_reference: status.externalReference,
        processing:false
      }));

      setStatusMessage('');
      setIsLoading(false);
    }
  });

  // 🔍 OBTENER DATOS DE PAGO SI NO VIENEN EN EL STATE
  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const fetchPaymentDetails = async (attempt = 0) => {
      // Intentar obtener datos desde los query params de MercadoPago
      const paymentId = searchParams.get('payment_id') || searchParams.get('collection_id');
      const externalRef = searchParams.get('external_reference');
      
      if (!state?.paymentDetails && (paymentId || externalRef)) {
        try {
          const reference = paymentId || externalRef;
          
          console.log(`🔍 Obteniendo detalles del pago: ${reference}`);
          setStatusMessage(attempt > 0 ? 'Seguimos verificando tu compra...' : 'Verificando tu pago...');
          
          const response = await fetch(`${getApiBaseUrl()}/payments/payment-status/${reference}`);
          const data = await response.json().catch(() => null);
          
          if (cancelled) return;

          if (response.ok && data) {
            console.log('✅ Datos del pago obtenidos:', data);
            
            if (data.success && data.payment) {
              setPaymentDetails(data.payment);
              setStatusMessage(data.message || '');

              const shouldRetry = Boolean(data.payment.processing) && !data.payment.manual_review && attempt < 24;
              if (shouldRetry) {
                setIsLoading(false);
                retryTimer = setTimeout(() => fetchPaymentDetails(attempt + 1), 2500);
                return;
              }
            }
          } else {
            console.error('❌ Error al obtener detalles del pago:', response.status);
            setStatusMessage('No pudimos verificar el pago todavía. Intentá actualizar la página en unos segundos.');
          }
        } catch (error) {
          console.error('❌ Error al obtener detalles del pago:', error);
          if (!cancelled) setStatusMessage('No pudimos verificar el pago todavía. Intentá actualizar la página en unos segundos.');
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      } else if (!state?.paymentDetails) {
        // Si no hay datos en absoluto, redirigir al home
        setTimeout(() => navigate('/'), 3000);
      } else {
        setIsLoading(false);
      }
    };

    fetchPaymentDetails();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [state?.paymentDetails, searchParams, navigate]);

  // 📥 DESCARGAR COMPROBANTE PDF
  const handleDownloadReceipt = () => {
    window.print();
  };

  // 📥 DESCARGAR DESDE MERCADOPAGO
  const handleDownloadMercadoPago = () => {
    if (!paymentDetails?.mercadopago_payment_id) return;
    
    // Abrir el comprobante oficial de MercadoPago en nueva pestaña
    const mpReceiptUrl = `https://www.mercadopago.com.ar/activities?id=${paymentDetails.mercadopago_payment_id}`;
    window.open(mpReceiptUrl, '_blank');
  };

  // 🏠 VOLVER AL HOME
  const handleGoHome = () => {
    navigate('/');
  };

  // 📊 FORMATO DE MONEDA
  const formatCurrency = (amount: number, currency: string = 'ARS') => {
    const symbols: { [key: string]: string } = {
      'ARS': '$',
      'USD': 'US$',
      'BRL': 'R$',
      'MXN': 'MX$',
      'CLP': 'CL$'
    };
    return `${symbols[currency] || currency} ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // 📅 FORMATO DE FECHA
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const resolveAssetUrl = (value?: string) => {
    if (!value) return '';
    if (/^(https?:|data:|blob:)/i.test(value)) return value;
    return `${getApiBaseUrl().replace(/\/api\/?$/, '')}${value.startsWith('/') ? value : `/${value}`}`;
  };

  const isManualReview = Boolean(
    paymentDetails?.manual_review ||
    paymentDetails?.order?.manualReview?.required ||
    ['approved_stock_conflict', 'manual_review'].includes(paymentDetails?.order?.status || '')
  );
  const isProcessing = Boolean(paymentDetails?.processing && !isManualReview);
  const hasTickets = Boolean(paymentDetails?.tickets?.length);
  const reviewMessage = paymentDetails?.userMessage || paymentDetails?.order?.manualReview?.message || 'Tus entradas están en revisión. Te contactaremos por email.';
  const headerTitle = isManualReview ? 'Pago aprobado' : isProcessing ? 'Estamos preparando tus entradas' : '¡Pago exitoso!';
  const headerMessage = isManualReview
    ? 'Tus entradas están en revisión. Te contactaremos por email.'
    : isProcessing
      ? (statusMessage || 'Estamos confirmando los últimos datos de tu compra.')
      : message;
  const statusLabel = isManualReview ? 'Entradas en revisión' : isProcessing ? 'Procesando entradas' : 'Aprobado';
  const statusColor = isManualReview
    ? 'bg-amber-50 border-amber-200 text-amber-800'
    : isProcessing
      ? 'bg-blue-50 border-blue-200 text-blue-800'
      : 'bg-green-50 border-green-200 text-green-800';
  const statusDot = isManualReview ? 'bg-amber-500' : isProcessing ? 'bg-blue-500' : 'bg-green-500';

  // ⏳ LOADING
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[linear-gradient(180deg,#f8fafc_0%,#eef2f6_100%)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-zen-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Cargando comprobante...</p>
        </div>
      </div>
    );
  }

  if (!paymentDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[linear-gradient(180deg,#f8fafc_0%,#eef2f6_100%)]">
        <div className="text-center">
          <p className="text-gray-600 mb-4">No se encontraron detalles del pago</p>
          <p className="text-sm text-gray-500">Redirigiendo al inicio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f7fbf8_0%,#f5f8fc_100%)] px-4 py-8">
      <div className="max-w-4xl mx-auto">
        
        {/* 🎉 HEADER DE ÉXITO */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-green-500 rounded-full mb-4 shadow-lg"
          >
            <CheckCircle className="w-12 h-12 text-white" />
          </motion.div>
          
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              {headerTitle}
            </h1>
            <p className="text-lg text-gray-600">
              {headerMessage}
            </p>
          </motion.div>

        {/* 🧾 COMPROBANTE */}
        <motion.div
          ref={printRef}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="overflow-hidden rounded-[2rem] border border-white/80 bg-white/95 shadow-[0_34px_80px_-40px_rgba(15,23,42,0.24)] print:shadow-none"
        >
          
          {/* 📋 HEADER DEL COMPROBANTE */}
          <div className="bg-[linear-gradient(135deg,#020617_0%,#111827_100%)] text-white p-6 md:p-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="mb-1 text-2xl font-bold">{PEEKPASS_BRAND}</h2>
                <p className="text-zen-100 text-sm">Comprobante de Pago</p>
              </div>
              <FileText className="w-12 h-12 text-zen-200" />
            </div>
          </div>

          {/* ✅ ESTADO DEL PAGO */}
            <div className={`${statusColor} border-b-2 px-6 md:px-8 py-4`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 ${statusDot} rounded-full animate-pulse`}></div>
                  <span className="font-semibold text-lg">
                    Estado: {statusLabel}
                  </span>
                </div>
                <span className="text-xs bg-white/70 px-3 py-1 rounded-full">
                  {(paymentDetails.order?.status || paymentDetails.status).toUpperCase()}
                </span>
              </div>
            </div>

          {/* 📊 INFORMACIÓN PRINCIPAL */}
          <div className="p-6 md:p-8 space-y-6">
            
            {/* 💰 MONTO TOTAL */}
            <div className="text-center py-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border-2 border-blue-200">
              <p className="text-sm text-gray-600 font-medium mb-1">Monto Total</p>
              <p className="text-4xl font-bold text-gray-900">
                {formatCurrency(paymentDetails.amount, paymentDetails.currency)}
              </p>
              {paymentDetails.transaction_details?.installments && paymentDetails.transaction_details.installments > 1 && (
                <p className="text-sm text-gray-500 mt-2">
                  en {paymentDetails.transaction_details.installments} cuota{paymentDetails.transaction_details.installments > 1 ? 's' : ''}
                </p>
              )}
            </div>

            {/* 📦 ITEMS COMPRADOS */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Package className="w-5 h-5 text-zen-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Entradas adquiridas</h3>
                </div>
                
                {paymentDetails.items.length ? (
                  <div className="space-y-3">
                    {paymentDetails.items.map((item, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                        <h4 className="font-semibold text-gray-800">{item.title}</h4>
                        {item.description && (
                          <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          Cantidad: {item.quantity} × {formatCurrency(item.unit_price, paymentDetails.currency)}
                        </p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="font-bold text-gray-900">
                          {formatCurrency(item.total, paymentDetails.currency)}
                        </p>
                        </div>
                      </div>
                    </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm font-medium text-blue-800">
                    Estamos sincronizando el detalle de tu compra con Mercado Pago.
                  </div>
                )}
              </div>

              {paymentDetails.order?.priceBreakdown ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <h3 className="mb-3 text-lg font-semibold text-gray-800">Detalle del importe</h3>
                  <div className="space-y-2 text-sm">
                  <div className="flex justify-between gap-4"><span className="text-gray-600">Valor de la entrada</span><span className="font-semibold">{formatCurrency(paymentDetails.order.priceBreakdown.ticketSubtotal || paymentDetails.order.organizerAmount || 0, paymentDetails.currency)}</span></div>
                  <div className="flex justify-between gap-4"><span className="text-gray-600">Cargo por servicio</span><span className="font-semibold">{formatCurrency(paymentDetails.order.priceBreakdown.serviceFeeAmount || paymentDetails.order.serviceFeeAmount || 0, paymentDetails.currency)}</span></div>
                  <div className="flex justify-between gap-4 border-t border-gray-200 pt-3 text-base"><span className="font-bold text-gray-900">Total pagado</span><span className="font-bold text-gray-900">{formatCurrency(paymentDetails.order.priceBreakdown.totalPaid || paymentDetails.order.totalPaid || paymentDetails.amount, paymentDetails.currency)}</span></div>
                  </div>
                </div>
              ) : null}

              {isManualReview ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
                  <div className="flex items-start gap-3">
                    <TicketIcon className="mt-1 h-5 w-5 shrink-0 text-amber-700" />
                    <div>
                      <h3 className="text-lg font-bold text-amber-900">Tus entradas están en revisión</h3>
                      <p className="mt-2 text-sm leading-6 text-amber-800">{reviewMessage}</p>
                      {paymentDetails.order?.manualReview?.conflicts?.length ? (
                        <div className="mt-4 space-y-2 text-sm text-amber-900">
                          {paymentDetails.order.manualReview.conflicts.map((conflict, index) => (
                            <p key={`${conflict.eventTitle || 'conflict'}-${index}`}>
                              {conflict.eventTitle || 'Evento'} · {conflict.ticketTierName || 'Entrada'}: solicitadas {conflict.requested ?? '-'}, disponibles {conflict.available ?? '-'}.
                            </p>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}

              {isProcessing ? (
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-5">
                  <div className="flex items-start gap-3">
                    <div className="mt-1 h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-blue-200 border-t-blue-700" />
                    <div>
                      <h3 className="text-lg font-bold text-blue-900">Estamos preparando tus entradas</h3>
                      <p className="mt-2 text-sm leading-6 text-blue-800">
                        Tu pago fue recibido y estamos terminando de sincronizar la compra. Esta pantalla se actualizará automáticamente.
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              {hasTickets && !isManualReview ? (
                <div>
                  <div className="mb-4 flex items-center space-x-2">
                    <TicketIcon className="h-5 w-5 text-zen-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Tus entradas</h3>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                    {(paymentDetails.tickets || []).map((ticket) => (
                    <a key={ticket.ticketId} href={ticket.qrUrl || `/ticket/${ticket.ticketId}`} className="group flex gap-4 rounded-xl border border-gray-200 bg-white p-4 transition hover:border-[#71931b] hover:shadow-md">
                      <div className="grid h-20 w-20 shrink-0 place-items-center rounded-lg bg-gray-50">
                        {ticket.qrImageUrl ? <img src={resolveAssetUrl(ticket.qrImageUrl)} alt={`QR ${ticket.ticketId}`} className="h-full w-full object-contain p-1" /> : <QrCode className="h-9 w-9 text-gray-400" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#71931b]">{ticket.ticketTierName || 'General'}</p>
                        <p className="mt-1 truncate font-bold text-gray-900">{ticket.eventTitle}</p>
                        <p className="mt-1 text-xs text-gray-500">{ticket.ticketId}</p>
                        <p className="mt-2 text-xs font-semibold text-gray-600 group-hover:text-[#536d12]">Abrir entrada</p>
                      </div>
                    </a>
                  ))}
                  </div>
                </div>
              ) : null}

            {/* 🔖 DETALLES DE LA TRANSACCIÓN */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6 border-t border-gray-200">
              
              {/* ID de Pago */}
              <div className="flex items-start space-x-3">
                <Hash className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 font-medium mb-1">ID de Pago</p>
                  <p className="text-sm text-gray-900 font-mono break-all">{paymentDetails.mercadopago_payment_id}</p>
                </div>
              </div>

              {/* Referencia Externa */}
              <div className="flex items-start space-x-3">
                <Hash className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 font-medium mb-1">Referencia</p>
                  <p className="text-sm text-gray-900 font-mono break-all">{paymentDetails.external_reference}</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 font-medium mb-1">Email</p>
                  <p className="text-sm text-gray-900 break-all">{paymentDetails.user_email}</p>
                </div>
              </div>

              {/* Fecha de Aprobación */}
              <div className="flex items-start space-x-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 font-medium mb-1">Fecha de Aprobación</p>
                  <p className="text-sm text-gray-900">
                    {formatDate(paymentDetails.date_approved || paymentDetails.date_created)}
                  </p>
                </div>
              </div>

              {/* Método de Pago */}
              {paymentDetails.payment_method_id && (
                <div className="flex items-start space-x-3">
                  <CreditCard className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 font-medium mb-1">Método de Pago</p>
                    <p className="text-sm text-gray-900 capitalize">
                      {paymentDetails.payment_type_name || paymentDetails.payment_method_id}
                    </p>
                  </div>
                </div>
              )}

            </div>

              {/* 📧 NOTA DE CONFIRMACIÓN */}
              <div className={`${isManualReview ? 'bg-amber-50 border-amber-500' : 'bg-blue-50 border-blue-500'} border-l-4 rounded-r-lg p-4 mt-6`}>
                <p className={`text-sm ${isManualReview ? 'text-amber-800' : 'text-blue-800'}`}>
                  <span className="font-semibold">{isManualReview ? 'Revisión manual:' : 'Confirmación:'}</span>{' '}
                  {isManualReview
                    ? 'Te vamos a contactar por email para resolver la disponibilidad de tus entradas.'
                    : 'También vas a recibir un email con los detalles de tu compra.'}
                </p>
              </div>

          </div>

        </motion.div>

        {/* 🔽 BOTONES DE ACCIÓN */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-8 space-y-4 print:hidden"
        >
          
          {/* Botones principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Descargar Comprobante (Imprimir) */}
            <button
              onClick={handleDownloadReceipt}
              className="flex items-center justify-center space-x-2 bg-zen-600 text-white py-4 px-6 rounded-xl font-semibold hover:bg-zen-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <Download className="w-5 h-5" />
              <span>Descargar Comprobante</span>
            </button>

            {/* Ver en Mercado Pago */}
            <button
              onClick={handleDownloadMercadoPago}
              className="flex items-center justify-center space-x-2 bg-blue-500 text-white py-4 px-6 rounded-xl font-semibold hover:bg-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <FileText className="w-5 h-5" />
              <span>Ver en Mercado Pago</span>
            </button>
          </div>

          {/* Volver al Home */}
          <button
            onClick={handleGoHome}
            className="w-full flex items-center justify-center space-x-2 bg-white text-gray-700 py-4 px-6 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-200 shadow-md border-2 border-gray-200 hover:border-zen-300"
          >
            <Home className="w-5 h-5" />
            <span>Volver a {PEEKPASS_BRAND}</span>
          </button>

        </motion.div>

        {/* ℹ️ INFORMACIÓN ADICIONAL */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="mt-8 text-center text-sm text-gray-500 print:hidden"
        >
          <p>¿Necesitas ayuda? Contacta a soporte@peakpass.club</p>
          <p className="mt-2">© 2026 {PEEKPASS_BRAND} - Todos los derechos reservados</p>
        </motion.div>

      </div>

      {/* 🖨️ ESTILOS DE IMPRESIÓN */}
      <style>{`
        @media print {
          body {
            background: white !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PaymentSuccess;
